import { Link } from "react-router-dom";
import RevealOnScroll from "../components/RevealOnScroll";
import { BRAND_ICON } from "../config/brand";
import { useBrand } from "../context/BrandContext";
import useParallaxScroll from "../hooks/useParallaxScroll";
import "./SalonBusinessCpaLanding.css";

const imageBase = "/SalonBusinessCPA Images";

const focusRingClass =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500";

const primaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 sm:w-auto ${focusRingClass}`;
const secondaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50 sm:w-auto ${focusRingClass}`;
const navLoginButtonClass = `inline-flex h-10 items-center justify-center rounded-md border border-[#44529b] bg-[#2f3566] px-4 text-sm font-semibold text-white transition hover:bg-[#252b57] ${focusRingClass}`;
const navRegisterButtonClass = `inline-flex h-10 items-center justify-center rounded-md bg-rose-500 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 ${focusRingClass}`;
const cardClass =
  "min-w-0 rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]";
const smallCardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]";

const serviceCards = [
  {
    title: "Tax readiness",
    body:
      "Keep personal tax, business tax, corporate tax, GST/HST, and year-end records organized before deadlines feel rushed.",
  },
  {
    title: "Payroll and owner pay",
    body:
      "Plan around employees, owner wages, tips, commissions, bonuses, dividends, and payroll remittance needs.",
  },
  {
    title: "Bookkeeping cleanup",
    body:
      "Turn receipts, bank activity, service sales, product sales, and payment reports into clearer monthly records.",
  },
];

const clientGroups = [
  {
    label: "Employee beauty professionals",
    body: "Personal tax support for T4 income, tips, commissions, bonuses, and salon-related records.",
  },
  {
    label: "Independent professionals",
    body: "Simple guidance for sole proprietors or incorporated professionals with business income, expenses, GST/HST, and owner pay questions.",
  },
  {
    label: "Salon owners",
    body: "Accounting support for teams, payroll, GST/HST, product sales, service revenue, financial statements, and year-end planning.",
  },
];

const comparisonCards = [
  {
    label: "Generic accounting software",
    title: "Usually starts with charts of accounts and bank feeds.",
    items: [
      "You still need to translate salon activity into accounting categories.",
      "Tips, commissions, booth rentals, and owner pay can be confusing.",
      "Checklists are not usually built around beauty business workflows.",
    ],
  },
  {
    label: "SalonBusinessCPA",
    title: "Starts with how beauty businesses actually work.",
    tone: "rose",
    items: [
      "Profiles for employees, independent professionals, and salon owners.",
      "Checklists for GST/HST, payroll, bookkeeping, personal tax, corporate tax, and financial statements.",
      "Built to work alongside Ellyanna Booking records and salon operations.",
    ],
  },
];

const workflowSteps = [
  {
    title: "Choose your profile",
    body:
      "Tell SalonBusinessCPA whether you are an employee, independent professional, or salon owner so the portal can show the right next steps.",
  },
  {
    title: "Upload the right records",
    body:
      "Send statements, receipts, CRA letters, payroll records, tax slips, and other documents in one organized place.",
  },
  {
    title: "Follow clear checklists",
    body:
      "Use simple task lists for GST/HST, payroll, bookkeeping, tax filing, and financial statement preparation.",
  },
];

const moduleCards = [
  "Personal tax",
  "Corporate tax",
  "GST/HST",
  "Payroll",
  "Bookkeeping",
  "Financial statements",
  "Tips and commissions",
  "Dividends and owner pay",
];

function LogoWordmark() {
  return (
    <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Ellyanna SalonBusinessCPA home">
      <img
        src={BRAND_ICON}
        alt=""
        className="h-11 w-11 shrink-0 rounded-2xl shadow-[0_10px_24px_rgba(8,12,40,0.2)]"
      />
      <span className="min-w-0 leading-tight">
        <span className="block text-lg font-semibold tracking-tight text-white">
          Ellyanna
        </span>
        <span className="block text-[10px] font-semibold uppercase tracking-[0.32em] text-white/78">
          SalonBusinessCPA
        </span>
      </span>
    </Link>
  );
}

function Badge({ children }) {
  return (
    <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
      {children}
    </p>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="m5 12.5 4.2 4.2L19 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeroVisual({ parallaxStyle }) {
  return (
    <div className={`${cardClass} salon-cpa-hero-visual relative overflow-hidden p-5 md:p-6`}>
      <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-slate-950">
        <div className="aspect-[16/11] min-h-[300px]">
          <img
            src={`${imageBase}/6-SBA image.png`}
            alt="Salon owner reviewing SalonBusinessCPA business performance in a bright nail salon."
            className="h-full w-full object-cover opacity-90"
            loading="eager"
            style={parallaxStyle}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/22 via-slate-950/5 to-rose-500/18" />
        <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/25 bg-white/90 p-4 shadow-2xl backdrop-blur">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
            Accounting dashboard
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            {["GST/HST", "Payroll", "Tax ready"].map((item) => (
              <div key={item} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-xs font-semibold text-slate-950">{item}</p>
                <p className="mt-1 h-1.5 rounded-full bg-rose-100">
                  <span className="block h-1.5 w-2/3 rounded-full bg-rose-400" />
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="relative mt-4 grid gap-3 md:grid-cols-3">
        {serviceCards.map((card, index) => (
          <div
            key={card.title}
            className="salon-cpa-float-soft rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_38px_rgba(47,53,102,0.08)]"
            style={{ animationDelay: `${index * 320}ms` }}
          >
            <h3 className="text-sm font-semibold text-slate-950">{card.title}</h3>
            <p className="mt-2 text-xs leading-5 text-slate-600">{card.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientGroupCard({ label, body, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
        <CheckIcon />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{label}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function WorkflowCard({ title, body, index }) {
  return (
    <RevealOnScroll
      as="article"
      delay={index * 120}
      className={smallCardClass}
    >
      <p className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
        {index + 1}
      </p>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function ComparisonCard({ label, title, items, tone }) {
  const isRose = tone === "rose";
  return (
    <RevealOnScroll
      as="article"
      className={`rounded-2xl border p-5 ${
        isRose ? "border-rose-200 bg-rose-50/80" : "border-slate-200 bg-slate-50"
      }`}
    >
      <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isRose ? "text-rose-500" : "text-slate-500"}`}>
        {label}
      </p>
      <h3 className="mt-3 text-xl font-semibold text-slate-950">{title}</h3>
      <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
        {items.map((item) => (
          <li key={item} className="flex gap-3">
            <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isRose ? "bg-rose-400" : "bg-slate-400"}`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </RevealOnScroll>
  );
}

function ImageCard({ image, title, body, alt, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={`${imageBase}/${image}`} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </RevealOnScroll>
  );
}

export default function SalonBusinessCpaLanding() {
  const brand = useBrand();
  const parallax = useParallaxScroll();
  const portalPath = "/salonbusinesscpa";
  const loginUrl = `/login?next=${encodeURIComponent(portalPath)}`;
  const registerUrl = `${brand?.opsHost ? `https://${brand.opsHost}` : "https://ellyanna.com"}/register`;

  return (
    <main className="salon-cpa-home-surface min-h-screen bg-[linear-gradient(135deg,#fff8f8_0%,#f5f7ff_52%,#ffffff_100%)] text-slate-950">
      <header className="sticky top-0 z-30 border-b border-[#22284f] bg-[#2f3566] text-white shadow-sm">
        <div className="mx-auto flex min-h-[68px] max-w-6xl items-center justify-between gap-4 px-5 py-3">
          <LogoWordmark />
          <nav className="hidden items-center gap-6 text-sm font-semibold text-white/78 lg:flex" aria-label="SalonBusinessCPA navigation">
            <a href="#services" className="transition hover:text-white">
              Services
            </a>
            <a href="#who-it-helps" className="transition hover:text-white">
              Who it helps
            </a>
            <a href="#why-different" className="transition hover:text-white">
              Why different
            </a>
          </nav>
          <div className="flex shrink-0 items-center gap-2">
            <a href={loginUrl} className={navLoginButtonClass}>
              Login
            </a>
            <a href={registerUrl} className={navRegisterButtonClass}>
              Register
            </a>
          </div>
        </div>
      </header>

      <div className="relative mx-auto max-w-6xl space-y-7 px-5 py-8 md:py-10">
        <section className="grid gap-5 lg:grid-cols-[1.02fr,0.98fr] lg:items-stretch" aria-labelledby="salon-cpa-hero-title">
          <RevealOnScroll className={`${cardClass} p-6 md:p-8`}>
            <Badge>Ellyanna SalonBusinessCPA</Badge>
            <h1
              id="salon-cpa-hero-title"
              className="mt-6 max-w-3xl break-words text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl md:leading-tight"
            >
              Accounting, tax, payroll, and bookkeeping support built for Canadian beauty businesses.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
              SalonBusinessCPA helps nail technicians, independent professionals, and salon owners stay organized for tax time, GST/HST, payroll, owner pay, bookkeeping, and financial reporting.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a href={loginUrl} className={primaryButtonClass}>
                Log in to SalonBusinessCPA
              </a>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-500">
              Use your Ellyanna Booking credentials on the SalonBusinessCPA login screen. Available for active Ellyanna Booking subscribers, with guided records and checklists made for salon work.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={120} className="min-w-0">
            <HeroVisual parallaxStyle={parallax.mid} />
          </RevealOnScroll>
        </section>

        <section className="grid gap-4 md:grid-cols-3" aria-label="SalonBusinessCPA service preview cards" id="services">
          <ImageCard
            image="5-SBA image.png"
            title="From messy records to calm next steps"
            body="Receipts, invoices, tax forms, and notes can pile up quickly. SalonBusinessCPA turns the work into clear document requests and simple checklists."
            alt="Independent beauty professional surrounded by receipts and salon business paperwork."
          />
          <ImageCard
            image="4-SBA image.png"
            title="Support that understands salon revenue"
            body="Service sales, product sales, tips, commissions, workspace rental, staff pay, and owner income all need accounting logic that fits the salon."
            alt="Accounting advisor reviewing records with a salon professional in a busy nail salon."
            delay={120}
          />
          <ImageCard
            image="3-SBA image.png"
            title="Canadian tax and payroll focus"
            body="Plan around GST/HST, payroll remittances, T4 income, corporate tax, personal tax, and shareholder or dividend questions."
            alt="Canadian salon business meeting with a tax advisor reviewing reports."
            delay={240}
          />
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} id="who-it-helps" aria-labelledby="who-it-helps-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="who-it-helps-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              Made for the way beauty professionals earn, pay, and file.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              SalonBusinessCPA starts with your working profile, then shows the records and checklists that fit your situation.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {clientGroups.map((group, index) => (
              <ClientGroupCard key={group.label} {...group} delay={index * 120} />
            ))}
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.95fr,1.05fr] lg:items-center" aria-labelledby="modules-title">
          <RevealOnScroll className={smallCardClass}>
            <Badge>What gets organized</Badge>
            <h2 id="modules-title" className="mt-5 break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              One place for the accounting work salons keep putting off.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              The goal is not to make salon owners learn accounting language. The goal is to show what is needed, why it matters, and what happens next.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {moduleCards.map((module) => (
                <div key={module} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                    <CheckIcon />
                  </span>
                  <span>{module}</span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll delay={120} className="overflow-hidden rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]">
            <img
              src={`${imageBase}/SBA Group images.png`}
              alt="SalonBusinessCPA scenes showing salon tax, bookkeeping, payroll, and accounting support."
              className="h-full min-h-[360px] w-full object-cover"
              loading="lazy"
            />
          </RevealOnScroll>
        </section>

        <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="workflow-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="workflow-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              A simpler workflow than handing over a box of receipts.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Each step is designed to feel practical for a busy salon owner or beauty professional.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {workflowSteps.map((step, index) => (
              <WorkflowCard key={step.title} {...step} index={index} />
            ))}
          </div>
        </section>

        <section className="px-1 py-3 md:px-2" id="why-different" aria-labelledby="why-different-title">
          <RevealOnScroll className="max-w-3xl">
            <h2 id="why-different-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
              Different from generic bookkeeping tools.
            </h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              SalonBusinessCPA is built around salon decisions first, then accounting categories second.
            </p>
          </RevealOnScroll>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {comparisonCards.map((card) => (
              <ComparisonCard key={card.label} {...card} />
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br from-white via-rose-50 to-indigo-50 px-6 py-8 shadow-[0_24px_80px_rgba(47,53,102,0.12)] md:px-8" aria-labelledby="final-cta-title">
          <RevealOnScroll className="grid gap-5 lg:grid-cols-[1fr,auto] lg:items-center">
            <div>
              <h2 id="final-cta-title" className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
                Keep the salon work moving while the accounting stays organized.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Sign in with Ellyanna to open your SalonBusinessCPA portal, complete your profile, upload records, and start with the checklist that fits your business.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <div className="salon-cpa-float-soft-slow rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-lg" aria-hidden="true">
                Profile, documents, checklists
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap lg:justify-end">
                <a href={loginUrl} className={primaryButtonClass}>
                  Log in to SalonBusinessCPA
                </a>
                <a href="https://ellyanna.com" className={secondaryButtonClass}>
                  Visit Ellyanna Booking
                </a>
              </div>
            </div>
          </RevealOnScroll>
        </section>
      </div>
    </main>
  );
}
